/**
 * RegisterPage — split layout matching the Figma.
 *
 * Backend contract unchanged. Submits `{ name, email, mobile, password,
 * country_id }` to `authService.register(...)`. The mobile field is
 * composed in the UI from a country-code chip (driven by `country_id`)
 * and a national-number input; we join them into an E.164-ish string
 * before submission so the backend gets a single value.
 *
 * Fields shown (matching the Figma plus what the backend already wants):
 *   User name        → name        (required)
 *   Company name     → optional visual-only field; NOT submitted unless
 *                      the backend adds a column for it later.
 *                      Captured into `company` for future wiring; see
 *                      TODO(register-company).
 *   Phone            → mobile      (country code dropdown + number)
 *   Country          → country_id  (required)
 *   Email            → email       (required)
 *   Password         → password    (required, 8+ with mixed case + digit)
 *   Confirm password → client-side check, not sent
 *   Terms checkbox   → client-side gate on Register button
 */

import { useEffect, useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineLockClosed,
} from 'react-icons/hi2'
import { authService } from '../../services/authService'
import { Button, FormField, Input, Checkbox, Banner, IconButton } from '../../components/ui'
import {
  AuthShell,
  AuthHeading,
  PhoneInput,
} from '../../components/auth'

const registerSchema = z.object({
  name: z.string().min(1, 'User name is required').min(2, 'Name must be at least 2 characters'),
  company: z.string().optional(),
  country_id: z.string().min(1, 'Please select a country'),
  mobileNational: z
    .string()
    .min(1, 'Phone number is required')
    .regex(/^[0-9\-\s()]+$/, 'Invalid phone number format'),
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Must contain upper, lower, and a number'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
  agreeToS: z.literal(true, { errorMap: () => ({ message: 'You must agree to the Terms and Privacy Policy' }) }),
}).refine((d) => d.password === d.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
})

export default function RegisterPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const invitationToken = searchParams.get('invitation')

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [apiError, setApiError] = useState('')
  const [countries, setCountries] = useState([])
  const [loadingCountries, setLoadingCountries] = useState(true)

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
    mode: 'onBlur',
    defaultValues: { agreeToS: false },
  })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const response = await authService.getCountries()
        if (!cancelled && response.success) setCountries(response.data.data || [])
      } catch {
        // swallow — leave countries empty; user gets a "select a country" error
      } finally {
        if (!cancelled) setLoadingCountries(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const selectedCountryId = watch('country_id')
  const selectedCountry = countries.find((c) => String(c.id) === String(selectedCountryId))

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      setApiError('')

      // Join country code + national number into the single `mobile` value
      // the backend already expects.
      const mobile = PhoneInput.joinE164(selectedCountry?.phone_code, data.mobileNational)

      const payload = {
        name: data.name,
        email: data.email,
        mobile,
        password: data.password,
        country_id: parseInt(data.country_id, 10),
        // TODO(register-company): submit `company: data.company` once the
        // backend accepts it. For now collected but not persisted.
      }

      const response = await authService.register(payload)
      if (response.success) {
        navigate(`/verify-otp?email=${encodeURIComponent(data.email)}${
          invitationToken ? `&invitation=${invitationToken}` : ''
        }`)
      } else {
        setApiError(response.error?.message || 'Registration failed')
      }
    } catch {
      setApiError('Network error - please try again')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthShell
      variant="split"
      showBack
      backTo="/login"
    >
      <AuthHeading title={t('auth.register.title')} />

      {invitationToken && (
        <Banner tone="info" className="mb-5">
          You've been invited to OVACS — complete this form to accept the invitation.
        </Banner>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3.5">
        {apiError && <Banner tone="danger" title="Registration failed">{apiError}</Banner>}

        <FormField label={t('auth.register.name')} error={errors.name?.message} required>
          <Input autoComplete="name" placeholder={t('auth.register.namePlaceholder')} {...register('name')} />
        </FormField>

        <FormField label={t('auth.register.company')} error={errors.company?.message}>
          <Input autoComplete="organization" placeholder={t('auth.register.companyPlaceholder')} {...register('company')} />
        </FormField>

        <FormField label={t('auth.register.phone')} error={errors.mobileNational?.message || errors.country_id?.message} required>
          <Controller
            name="mobileNational"
            control={control}
            render={({ field }) => (
              <PhoneInput
                countries={countries}
                countryId={selectedCountryId || ''}
                onCountryChange={(id) => {
                  // forward to react-hook-form's country_id register
                  const setter = register('country_id').onChange
                  setter({ target: { name: 'country_id', value: id } })
                }}
                value={field.value || ''}
                onChange={field.onChange}
                invalid={!!(errors.mobileNational || errors.country_id)}
                loadingCountries={loadingCountries}
                placeholder="Phone number"
              />
            )}
          />
          {/* Hidden control so the form picks up country_id selection */}
          <input type="hidden" {...register('country_id')} />
        </FormField>

        <FormField label={t('auth.register.country')} error={errors.country_id?.message} required>
          <select
            {...register('country_id')}
            disabled={loadingCountries}
            className="w-full h-10 ps-3 pe-9 text-sm bg-surface text-ink-primary border border-border hover:border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors appearance-none disabled:opacity-60"
          >
            <option value="">{loadingCountries ? 'Loading countries…' : t('auth.register.countryPlaceholder')}</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.phone_code})</option>
            ))}
          </select>
        </FormField>

        <FormField label={t('auth.register.email')} error={errors.email?.message} required>
          <Input type="email" autoComplete="email" placeholder={t('auth.register.emailPlaceholder')} {...register('email')} />
        </FormField>

        <FormField label={t('auth.register.password')} error={errors.password?.message} required>
          <Input
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder={t('auth.register.passwordPlaceholder')}
            leadingIcon={HiOutlineLockClosed}
            trailingAction={
              <IconButton
                icon={showPassword ? HiOutlineEyeSlash : HiOutlineEye}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(s => !s)}
              />
            }
            {...register('password')}
          />
        </FormField>

        <FormField label={t('auth.register.confirmPassword')} error={errors.confirmPassword?.message} required>
          <Input
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder={t('auth.register.confirmPasswordPlaceholder')}
            leadingIcon={HiOutlineLockClosed}
            trailingAction={
              <IconButton
                icon={showConfirmPassword ? HiOutlineEyeSlash : HiOutlineEye}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                variant="ghost"
                size="sm"
                onClick={() => setShowConfirmPassword(s => !s)}
              />
            }
            {...register('confirmPassword')}
          />
        </FormField>

        <Controller
          name="agreeToS"
          control={control}
          render={({ field, fieldState }) => (
            <div className="pt-2">
              <Checkbox
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                invalid={!!fieldState.error}
              >
                <span className="text-ink-secondary">
                  {t('auth.register.agreePrefix')}{' '}
                  <Link to="/terms" className="font-semibold text-brand hover:text-brand-hover underline-offset-2 hover:underline">
                    {t('auth.register.termsLink')}
                  </Link>{' '}
                  {t('auth.register.andText')}{' '}
                  <Link to="/privacy" className="font-semibold text-brand hover:text-brand-hover underline-offset-2 hover:underline">
                    {t('auth.register.privacyLink')}
                  </Link>
                </span>
              </Checkbox>
              {fieldState.error && (
                <p role="alert" className="text-xs text-danger mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <div className="pt-3">
          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={loadingCountries}
          >
            {t('auth.register.submit')}
          </Button>
        </div>

        <p className="text-center text-sm text-ink-secondary">
          {t('auth.register.haveAccount')}{' '}
          <Link to="/login" className="font-semibold text-brand hover:text-brand-hover transition-colors">
            {t('auth.register.signInLink')}
          </Link>
        </p>
      </form>
    </AuthShell>
  )
}
